import { useEffect, useRef, useState } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import type { AppTab } from '../components/BottomNav';
import type { GameState } from '../game/types';
import { guidedTutorialStep, isHongKongBeginnerTutorial } from '../game/tutorial';

interface Options {
  game: GameState | null;
  tab: AppTab;
  mobileViewport: boolean;
  selectedAirportId?: string;
  routeOriginId?: string;
  routeTargetId?: string;
}

interface PersistedGuidePhase {
  weeklyFinanceComplete: boolean;
  advisorComplete: boolean;
  statementsComplete: boolean;
  fareGuideComplete: boolean;
  quarterlyComplete: boolean;
  achievementsComplete: boolean;
  aircraftGuideComplete: boolean;
}

const EMPTY_GUIDE_PHASE: PersistedGuidePhase = {
  weeklyFinanceComplete: false,
  advisorComplete: false,
  statementsComplete: false,
  fareGuideComplete: false,
  quarterlyComplete: false,
  achievementsComplete: false,
  aircraftGuideComplete: false,
};

function guidePhaseKey(game: GameState): string {
  return `noflight-guided-phase:${game.id}:${game.createdAt}`;
}

function readGuidePhase(game: GameState): PersistedGuidePhase {
  if (typeof window === 'undefined') return EMPTY_GUIDE_PHASE;
  try {
    const value = JSON.parse(window.localStorage.getItem(guidePhaseKey(game)) ?? '{}') as Partial<PersistedGuidePhase>;
    return {
      weeklyFinanceComplete: value.weeklyFinanceComplete === true,
      advisorComplete: value.advisorComplete === true,
      statementsComplete: value.statementsComplete === true,
      fareGuideComplete: value.fareGuideComplete === true,
      quarterlyComplete: value.quarterlyComplete === true,
      achievementsComplete: value.achievementsComplete === true,
      aircraftGuideComplete: value.aircraftGuideComplete === true,
    };
  } catch {
    return EMPTY_GUIDE_PHASE;
  }
}

function writeGuidePhase(game: GameState, phase: PersistedGuidePhase): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(guidePhaseKey(game), JSON.stringify(phase));
}

export function useGuidedTutorial({ game, tab, mobileViewport, selectedAirportId, routeOriginId, routeTargetId }: Options) {
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [tourRetry, setTourRetry] = useState(0);
  const [fleetIntroAcknowledged, setFleetIntroAcknowledged] = useState(false);
  const [financeIntroAcknowledged, setFinanceIntroAcknowledged] = useState(false);
  const [destinationProfileProgress, setDestinationProfileProgress] = useState<{ routeTargetId?: string; index: number }>({ index: 0 });
  const [pvgRouteReviewAcknowledged, setPvgRouteReviewAcknowledged] = useState(false);
  const [weeklyFinanceProgress, setWeeklyFinanceProgress] = useState<{ week?: number; index: number }>({ index: 0 });
  const [statementGuideProgress, setStatementGuideProgress] = useState<{ week?: number; index: number }>({ index: 0 });
  const [aircraftGuideProgress, setAircraftGuideProgress] = useState<{ week?: number; index: number }>({ index: 0 });
  const [guidePhase, setGuidePhase] = useState<PersistedGuidePhase>(EMPTY_GUIDE_PHASE);
  const [skipped, setSkipped] = useState(false);
  const [tutorialEnabled, setTutorialEnabledState] = useState(true);
  const [skipConfirmationOpen, setSkipConfirmationOpen] = useState(false);
  const tourRef = useRef<ReturnType<typeof driver> | null>(null);
  const currentStep = game ? guidedTutorialStep(game) : 'complete';
  const isGuidedRouteStep = currentStep === 'route-hkg-sha' || currentStep === 'route-hkg-pvg';

  useEffect(() => {
    setSkipped(false);
    setTutorialEnabledState(true);
    setFleetIntroAcknowledged(false);
    setFinanceIntroAcknowledged(false);
    setDestinationProfileProgress({ index: 0 });
    setPvgRouteReviewAcknowledged(false);
    setWeeklyFinanceProgress({ index: 0 });
    setStatementGuideProgress({ index: 0 });
    setAircraftGuideProgress({ index: 0 });
    setGuidePhase(game ? readGuidePhase(game) : EMPTY_GUIDE_PHASE);
    setSkipConfirmationOpen(false);
    setTourRetry(0);
    tourRef.current?.destroy();
    tourRef.current = null;
  }, [game?.createdAt, game?.id]);

  useEffect(() => {
    const refreshTutorial = () => setTourRetry((value) => value + 1);
    const onHashChange = refreshTutorial;
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('tutorial-achievements-open', refreshTutorial);
    window.addEventListener('tutorial-aircraft-open', refreshTutorial);
    window.addEventListener('tutorial-blocking-modal-change', refreshTutorial);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('tutorial-achievements-open', refreshTutorial);
      window.removeEventListener('tutorial-aircraft-open', refreshTutorial);
      window.removeEventListener('tutorial-blocking-modal-change', refreshTutorial);
    };
  }, []);

  useEffect(() => {
    if (import.meta.env.MODE === 'test') return;
    if (!tutorialEnabled || skipped) {
      tourRef.current?.destroy();
      tourRef.current = null;
      return;
    }
    if (document.querySelector('[data-tutorial-blocking-event-modal]')) {
      tourRef.current?.destroy();
      tourRef.current = null;
      return;
    }
    const hasBothTutorialRoutes = !!game
      && game.routes.some((route) => route.active && route.originId === 'HKG' && route.destinationId === 'SHA')
      && game.routes.some((route) => route.active && route.originId === 'HKG' && route.destinationId === 'PVG');
    const isHongKongBeginnerGame = !!game
      && game.scenarioId === 'sandbox'
      && game.singlePlayerDifficulty === 'beginner'
      && game.headquartersId === 'HKG';
    const isWeeklyFinanceGuide = !!game
      && game.scenarioId === 'sandbox'
      && game.singlePlayerDifficulty === 'beginner'
      && game.headquartersId === 'HKG'
      && hasBothTutorialRoutes
      && game.reports.length > 0
      && (weeklyFinanceProgress.week === undefined || weeklyFinanceProgress.week === game.week)
      && !guidePhase.weeklyFinanceComplete
      && weeklyFinanceProgress.index < 4;
    const isWeeklyRoundIntro = !!game
      && game.scenarioId === 'sandbox'
      && game.singlePlayerDifficulty === 'beginner'
      && game.headquartersId === 'HKG'
      && hasBothTutorialRoutes
      && game.reports.length === 0;
    const isAdvisorGuide = !!game
      && guidePhase.weeklyFinanceComplete
      && !guidePhase.advisorComplete
      && game.reports.length < 13;
    const isQuarterlyGuide = !!game
      && guidePhase.weeklyFinanceComplete
      && guidePhase.advisorComplete
      && guidePhase.statementsComplete
      && guidePhase.fareGuideComplete
      && !guidePhase.quarterlyComplete
      && game.reports.length >= 13;
    const isFareGuide = !!game
      && isHongKongBeginnerGame
      && guidePhase.weeklyFinanceComplete
      && guidePhase.advisorComplete
      && guidePhase.statementsComplete
      && game.week >= 9
      && game.reports.length > 0
      && !guidePhase.fareGuideComplete;
    const isStatementGuide = !!game
      && isHongKongBeginnerGame
      && guidePhase.weeklyFinanceComplete
      && guidePhase.advisorComplete
      && game.week >= 7
      && game.reports.length > 0
      && !guidePhase.statementsComplete;
    const isAchievementGuide = !!game
      && isHongKongBeginnerGame
      && guidePhase.weeklyFinanceComplete
      && guidePhase.advisorComplete
      && guidePhase.statementsComplete
      && guidePhase.fareGuideComplete
      && game.week >= 12
      && game.reports.length < 13
      && !guidePhase.achievementsComplete;
    const isAircraftGuide = !!game
      && isHongKongBeginnerGame
      && guidePhase.weeklyFinanceComplete
      && guidePhase.advisorComplete
      && guidePhase.statementsComplete
      && guidePhase.fareGuideComplete
      && guidePhase.quarterlyComplete
      && game.week >= 20
      && !guidePhase.aircraftGuideComplete;
    if (!game || (!isHongKongBeginnerTutorial(game) && !isWeeklyRoundIntro && !isWeeklyFinanceGuide && !isAdvisorGuide && !isStatementGuide && !isFareGuide && !isQuarterlyGuide && !isAchievementGuide && !isAircraftGuide)) {
      tourRef.current?.destroy();
      tourRef.current = null;
      return;
    }

    const step = guidedTutorialStep(game);
    if (step === 'complete' && !isWeeklyRoundIntro && !isWeeklyFinanceGuide && !isAdvisorGuide && !isStatementGuide && !isFareGuide && !isQuarterlyGuide && !isAchievementGuide && !isAircraftGuide) {
      tourRef.current?.destroy();
      tourRef.current = null;
      return;
    }

    const routeDestination = step === 'route-hkg-sha' ? 'SHA' : 'PVG';
    const routeDestinationName = routeDestination === 'SHA' ? '上海虹桥' : '上海浦东';
    const showFleetIntro = step === 'buy-arj-finance'
      && tab === 'fleet'
      && !purchaseDialogOpen
      && !fleetIntroAcknowledged;
    const showFinanceIntro = step === 'buy-arj-finance'
      && purchaseDialogOpen
      && !financeIntroAcknowledged;
    const destinationProfileIndex = destinationProfileProgress.routeTargetId === routeTargetId
      ? destinationProfileProgress.index
      : 0;
    const showDestinationProfileIntro = isGuidedRouteStep
      && step === 'route-hkg-sha'
      && !!routeTargetId
      && destinationProfileIndex < 4;
    const showPvgRouteReview = step === 'route-hkg-pvg'
      && tab === 'map'
      && !routeOriginId
      && !routeTargetId
      && !pvgRouteReviewAcknowledged;
    const weeklyFinanceIndex = weeklyFinanceProgress.week === game.week ? weeklyFinanceProgress.index : 0;
    const companyAdvisorSelected = typeof window !== 'undefined' && window.location.hash === '#company/advisor';
    const financeReportsSelected = typeof window !== 'undefined' && window.location.hash === '#finance/reports';
    const financeOverviewSelected = typeof window !== 'undefined' && window.location.hash === '#finance/overview';
    const achievementModalOpen = !!document.querySelector('[data-tutorial-achievement-modal]');
    const aircraftDetailOpen = !!document.querySelector('[data-tutorial-aircraft-detail]');
    const statementGuideIndex = statementGuideProgress.week === game.week ? statementGuideProgress.index : 0;
    const aircraftGuideIndex = aircraftGuideProgress.week === game.week ? aircraftGuideProgress.index : 0;
    const fareTargetRoute = game.routes.find((route) => route.active && route.aircraftIds.length > 0);
    const fareTargetValue = fareTargetRoute ? Math.round(fareTargetRoute.pendingFareRatio ?? fareTargetRoute.fareRatio ?? 100) : 100;
    const airportSelector = (iata: string) => mobileViewport
      ? `[data-airport-id="${iata}"]`
      : `.airport[data-airport-id="${iata}"] .airport-touch-target`;
    let selector = purchaseDialogOpen
      ? '[data-tutorial-purchase-confirm]'
      : `[data-tutorial-purchase="${step === 'buy-arj-finance' ? 'ARJ21-finance' : 'ATR72-used'}"]`;
    let title = step === 'buy-arj-finance' ? '第 1 步：采购第一架飞机' : '第 2 步：买一架二手飞机';
    let description = step === 'buy-arj-finance'
      ? '分期购买能节约大量现金，现金不充裕的时候能用成本并不低的杠杆来快速扩大规模。请点击下方高亮的“25%首付”按钮，然后确认采购 1 架 ARJ21-700。'
      : '二手飞机的初始价格更低，可以用较少现金补充运力，适合开局阶段控制投入；代价是机况通常低于新机，后续可能需要更多维护。理解这个取舍后，请点击下方高亮的“二手”按钮，然后确认买入 1 架 ATR 72-600。';
    let advanceOnClick = false;
    let showNextButton = false;
    const needsFleetEntry = !purchaseDialogOpen && step === 'buy-arj-finance' && tab !== 'fleet';
    const needsMapEntry = !purchaseDialogOpen
      && (step === 'route-hkg-sha' || step === 'route-hkg-pvg')
      && tab !== 'map';

    if (needsFleetEntry || needsMapEntry) {
      const entryTab = needsMapEntry ? 'map' : 'fleet';
      selector = `[data-tutorial-tab="${entryTab}"]`;
      title = needsMapEntry ? '第 3 步：开始规划航线' : '第 1 步：拥有第一架飞机';
      description = needsMapEntry
        ? '拥有飞机就可以开始规划航线了。请点击下方高亮的“航线地图”，开始规划香港到上海虹桥的航线。'
        : '请点击下方高亮的“机队采购”，开始购买你的第一架飞机。';
      advanceOnClick = true;
    }
    if (purchaseDialogOpen) {
      title = step === 'buy-arj-finance' ? '确认融资采购 ARJ21-700' : '确认买入二手 ATR 72-600';
      description = '请点击下方高亮的“确认采购 1 架”按钮。当前步骤完成前，其他操作已锁定。';
    }

    if (showFleetIntro) {
      selector = '[data-tutorial-aircraft-card="ARJ21"]';
      title = '先看懂机型参数';
      description = '先根据实际需要比较座位数、航程和每周拥有成本：座位数影响运力，航程决定能飞多远，每周拥有成本影响长期现金流。看完后点击“下一步”，再开始采购。';
      showNextButton = true;
    }

    if (showFinanceIntro) {
      selector = '[data-tutorial-finance-details]';
      title = '先看懂融资数字';
      description = '采购价格是飞机总价；首付是现在立即支付的金额；贷款本金是之后需要偿还的金额；设立费是贷款产生的一次性费用；每周还款会自动从现金中扣除，其中包含本金和利息。看完后点击“下一步”，再确认融资采购。';
      showNextButton = true;
    }

    if (!needsMapEntry && !needsFleetEntry && !purchaseDialogOpen && isGuidedRouteStep) {
      title = `第 ${step === 'route-hkg-sha' ? 3 : 4} 步：规划香港 → ${routeDestinationName}`;
      description = `按顺序点击香港、规划新航线、选择${routeDestinationName}，最后确认航线。`;
      if (!routeOriginId && selectedAirportId === 'HKG') {
        selector = '[data-tutorial-begin-route]';
        description = '香港已选中，请立即点击“规划新航线”继续。';
        advanceOnClick = true;
      } else if (!routeOriginId) {
        selector = airportSelector('HKG');
        description = '先点击地图上的香港（HKG）作为航线起点。';
        advanceOnClick = true;
      } else if (!routeTargetId) {
        selector = airportSelector(routeDestination);
        description = `请选择${routeDestinationName}（${routeDestination}）。`;
        advanceOnClick = true;
      } else if (!document.querySelector('[data-tutorial-route-confirm]')) {
        selector = airportSelector(routeDestination);
        description = `请选择${routeDestinationName}（${routeDestination}）。`;
        advanceOnClick = true;
      } else {
        selector = '[data-tutorial-route-confirm]';
        description = `确认使用当前空闲飞机开通香港 → ${routeDestinationName} 航线。`;
      }
    }

    if (showDestinationProfileIntro) {
      const profileSteps = [
        {
          id: 'market',
          title: '城市规模：影响总市场容量',
          description: '城市规模会参与航线基础需求计算，直接影响预计需求、可售座位的潜在客流上限，以及满座时可能产生的客票收入。规模越大，通常越值得投入更多座位，但最终仍受航程、票价和竞争影响。',
        },
        {
          id: 'business',
          title: '商务指数：影响商务与头等客流',
          description: '商务指数会提高商务舱和头等舱在城市画像中的潜在占比，并增强两地商务联系带来的基础需求。两舱票价更高：商务舱约为经济舱 3.5 倍，头等舱约为 9 倍，因此可能提高单位乘客收入；但两舱座位卖不出去时，也会造成运力浪费。',
        },
        {
          id: 'tourism',
          title: '旅游指数：影响休闲客流与旺季',
          description: '旅游指数会提高航线的休闲客流潜力，并参与季节性需求波动。旅游指数高的城市，旺季预计需求通常更强，但淡季也要留意载客率变化；它更主要影响经济舱和整体客流规模，不等同于商务舱需求。',
        },
        {
          id: 'international',
          title: '国际化：影响跨区域需求与两舱',
          description: '国际化程度会影响跨区域航线的需求系数，并提高商务舱和头等舱的潜在客流占比。国际化城市更容易支撑高价值跨区域客流，但票价越高时，经济舱需求下降通常更明显；应结合预计需求、各舱座位和载客率判断配置。',
        },
      ][destinationProfileIndex];
      selector = `[data-tutorial-city-metric="${profileSteps.id}"]`;
      title = `${profileSteps.title}`;
      description = `${profileSteps.description} 看完后点击“下一步”。`;
      showNextButton = true;
    }

    if (showPvgRouteReview) {
      selector = '[data-tutorial-map-workspace]';
      title = '复习一次新航线规划';
      description = '刚才我们已经完成了香港 → 上海虹桥。现在再复习一次完整流程：先点击香港作为起点，点击“规划新航线”，再选择上海浦东，最后选择飞机并投入开航。看完后点击“下一步”。';
      showNextButton = true;
    }

    if (isWeeklyFinanceGuide) {
      const financeSteps = [
        {
          selector: '[data-tutorial-finance-cash]',
          title: '第 1/4 步：现金',
          description: '现金是公司当前可以直接支付的资金，购买飞机、开通航线、维护飞机和承担每周支出都会消耗它。现金不是利润：账面上赚钱但现金不足时，仍可能无法继续经营。',
        },
        {
          selector: '[data-tutorial-finance-net-cash-flow]',
          title: '第 2/4 步：净现金流',
          description: '净现金流表示这一周现金实际增加还是减少，综合经营活动、投资活动和筹资活动，并包含贷款利息与偿还本金等现金变化。它直接解释了本周现金为什么变多或变少。注意单位：K代表1000，M代表1百万，B代表十亿。',
        },
        {
          selector: '[data-tutorial-finance-operating-profit]',
          title: '第 3/4 步：经营利润',
          description: '经营利润衡量航线运营和枢纽运营本身赚不赚钱，扣除了航班运营成本、枢纽成本等经营支出，但不等于现金变化；购买飞机、开航投入和偿还贷款本金会影响现金，却不会直接计入经营利润。',
        },
        {
          selector: '[data-tutorial-finance-runway]',
          title: '第 4/4 步：现金安全期',
          description: '现金安全期表示按当前每周承诺支出，现有现金大约还能支撑多少周。它不是预测利润，而是现金缓冲时间；安全期越短，越需要控制扩张、维护现金或提前融资。',
        },
      ][weeklyFinanceIndex];
      selector = financeSteps.selector;
      title = financeSteps.title;
      description = `${financeSteps.description} 看完后点击“下一步”。`;
      showNextButton = true;
    }

    if (isWeeklyRoundIntro) {
      selector = '[data-tutorial-advance-week]';
      title = '开始下一回合：推进一周';
      description = '这是一个按周推进的回合制经营游戏。每点击一次“下一周”，系统会统一结算航班收入、运营成本、贷款利息、偿还本金和现金变化，并生成本周周报；结算后再根据指标决定下一步经营动作。请点击“下一周”开始第一轮结算。';
      advanceOnClick = true;
    }

    if (isAdvisorGuide) {
      if (tab !== 'company') {
        selector = '[data-tutorial-tab="company"]';
        title = '进入公司运营';
        description = '前面的基础经营指标已经复习完了。点击“公司运营”，可以查看公司的日常管理、燃油、顾问和其他经营规则。';
        advanceOnClick = true;
      } else if (!companyAdvisorSelected) {
        selector = '[data-tutorial-company-advisor]';
        title = '打开运营顾问';
        description = '点击“运营顾问”，这里会持续整理游戏中的经营规则和机制说明，之后遇到疑问可以随时回来查看。';
        advanceOnClick = true;
      } else {
        selector = '[data-tutorial-operating-advisor]';
        title = '接下来由你自由经营';
        description = '你已经知道如何查看游戏规则。接下来可以自行推进回合、观察航线和现金变化；运营顾问会一直保留，随时可以回来复习。点击“下一步”后，引导将暂时停止，直到第一份季报生成。';
        showNextButton = true;
      }
    }

    if (isQuarterlyGuide) {
      if (tab !== 'finance') {
        selector = '[data-tutorial-tab="finance"]';
        title = '查看第一份季报';
        description = '第一份季报已经生成。请点击“财务中心”，我们一起复盘这段经营周期。';
        advanceOnClick = true;
      } else if (!financeReportsSelected) {
        selector = '[data-tutorial-finance-reports-tab]';
        title = '打开周期报告';
        description = '请点击“周期报告”查看第一份季报。季报会把一段时间的收入、成本、利润、现金流和经营指标汇总在一起。';
        advanceOnClick = true;
      } else {
        selector = '[data-tutorial-quarterly-report]';
        title = '读懂第一份季报';
        description = '季报不是单周结果，而是对一个季度经营周期的汇总。先看营业收入和净利润判断经营成果，再看现金流量表确认现金是否真正增加，最后结合载客率、未满足需求和现金安全期判断下一阶段应该扩张、调价还是保留现金。看完后点击“下一步”，之后继续自行经营。';
        showNextButton = true;
      }
    }

    if (isStatementGuide) {
      const statementSteps = [
        {
          id: 'income',
          title: '第 1/3 步：利润表',
          description: '利润表回答“这段时间经营赚不赚钱”。先看营业收入，再看航班运营、枢纽、折旧和利息等成本，最后看净利润；它适合判断航线和公司运营成果，但不代表现金一定增加。',
        },
        {
          id: 'balance',
          title: '第 2/3 步：资产负债表',
          description: '资产负债表回答“公司现在有什么、欠多少、真正属于股东多少”。总资产包括现金和飞机等资产，有息负债是贷款，所有者权益约等于总资产减负债；它适合判断资产规模、杠杆压力和资本结构。',
        },
        {
          id: 'cash-flow',
          title: '第 3/3 步：现金流量表',
          description: '现金流量表回答“现金为什么变多或变少”。经营活动反映航班和日常运营带来的现金，投资活动反映买飞机、开航线和建储油库等投入，筹资活动反映贷款和还本付息；它最适合判断公司能不能持续支付和扩张。三张表要结合看：利润看成果，资产负债表看家底和债务，现金流看现金生存能力。',
        },
      ][statementGuideIndex];
      if (tab !== 'finance') {
        selector = '[data-tutorial-tab="finance"]';
        title = '第 7 周：财务三张表';
        description = '这是经营的关键，建议系统看一次财务三张表。请点击“财务中心”，我们从利润表开始。';
        advanceOnClick = true;
      } else if (!financeOverviewSelected) {
        selector = '[data-tutorial-finance-overview-tab]';
        title = '打开财务概览';
        description = '请点击“总览”，三张表会同时展示在这里，方便你从经营成果、资本结构和现金变化三个角度复盘公司。';
        advanceOnClick = true;
      } else {
        selector = `[data-tutorial-financial-statement="${statementSteps.id}"]`;
        title = statementSteps.title;
        description = `${statementSteps.description} 看完后点击“下一步”。`;
        showNextButton = true;
      }
    }

    if (isFareGuide) {
      if (tab !== 'routes') {
        selector = '[data-tutorial-tab="routes"]';
        title = '第 9 周：调整航线票价';
        description = '航线已经运行了一段时间，现在练习一次收益管理。请点击“航线总览”，查看并调整航线票价。';
        advanceOnClick = true;
      } else {
        selector = '[data-tutorial-route-fare]';
        title = '练习调整航线票价';
        description = `建议把高亮航线的票价调整到 130%，再点击“下一步”。130%只是一次小幅提价测试的参考值，不是必须达到的目标：票价过高会让部分客户放弃出行或流向竞争对手，客流和收入不一定随价格一起上升。当前为 ${fareTargetValue}%。`;
        showNextButton = true;
      }
    }

    if (isAchievementGuide) {
      if (tab !== 'company') {
        selector = '[data-tutorial-tab="company"]';
        title = '第 12 周：认识成就系统';
        description = '经营到第 12 周后，成就系统正式开放。请点击“公司运营”，我们去查看它。';
        advanceOnClick = true;
      } else if (!achievementModalOpen) {
        selector = '[data-tutorial-achievements]';
        title = '打开成就档案';
        description = '点击“成就”查看经营里程碑。这里会记录你已经完成的挑战，也会显示还差多少进度可以解锁新的成就。';
        advanceOnClick = true;
      } else {
        selector = '[data-tutorial-achievement-modal]';
        title = '读懂成就档案';
        description = '顶部的完成进度表示已解锁成就占全部成就的比例；绿色进度条表示当前成就的完成情况。成就只记录和奖励你的经营表现，不会限制你继续推进。看完后点击“下一步”，之后继续自行经营。';
        showNextButton = true;
      }
    }

    if (isAircraftGuide) {
      const aircraftSteps = [
        {
          selector: '[data-tutorial-aircraft-condition]',
          title: '机况：看懂飞机寿命消耗',
          description: '机况不是只看一个维修百分比。组合寿命消耗会同时观察飞行小时和起降循环，并以两者中消耗比例更高的一项作为主导；飞行小时反映持续飞行时间，起降循环反映起飞与降落次数。机况越低，维护压力和运营风险越高。',
        },
        {
          selector: '[data-tutorial-aircraft-range]',
          title: '最大航程：决定能飞多远',
          description: '最大航程是机型一次航段可覆盖的距离上限。规划航线时，航线单程距离不能超过飞机最大航程；航线越长，往返班次和燃油、飞行小时也会变化，所以要同时比较航程、需求和单位成本。',
        },
        {
          selector: '[data-tutorial-aircraft-runway]',
          title: '跑道等级：机场是否接得住',
          description: '跑道等级表示飞机对机场跑道条件的要求。飞机的要求等级必须不高于航线两端机场的跑道等级，否则不能在该航线上运行；选机时要同时检查起点和终点，不要只看最大航程。',
        },
        {
          selector: '[data-tutorial-aircraft-fare]',
          title: '本机票价：单独做收益测试',
          description: '这里可以只调整这一架飞机的票价，下一周生效，不会改变同一航线其他飞机的票价。适合测试不同飞机、不同舱位或不同服务定位；而“航线总览”里的统一票价会覆盖这条航线的全部飞机。',
        },
      ];
      if (aircraftGuideIndex >= aircraftSteps.length) {
        const nextPhase = { ...guidePhase, aircraftGuideComplete: true };
        setGuidePhase(nextPhase);
        writeGuidePhase(game, nextPhase);
        tourRef.current?.destroy();
        tourRef.current = null;
        return;
      }
      if (tab !== 'fleet') {
        selector = '[data-tutorial-tab="fleet"]';
        title = '第 20 周：复习如何查看飞机';
        description = '机队规模扩大后，要学会单独检查每架飞机的状态。请点击“机队采购”。';
        advanceOnClick = true;
      } else if (!aircraftDetailOpen) {
        selector = '[data-tutorial-aircraft-entry]';
        title = '打开飞机详情';
        description = '请点击高亮飞机的名称或飞机图标，都可以打开这架飞机的详情页面。';
        advanceOnClick = true;
      } else {
        const detailStep = aircraftSteps[aircraftGuideIndex];
        selector = detailStep.selector;
        title = detailStep.title;
        description = `${detailStep.description} 看完后点击“下一步”。`;
        showNextButton = true;
      }
    }

    if (!document.querySelector(selector)) {
      const retry = window.setTimeout(() => setTourRetry((value) => value + 1), 100);
      return () => window.clearTimeout(retry);
    }

    tourRef.current?.destroy();
    const tour = driver({
      showProgress: false,
      allowClose: true,
      allowKeyboardControl: false,
      overlayOpacity: 0.45,
      overlayClickBehavior: () => {},
      disableActiveInteraction: showFleetIntro || showFinanceIntro || showDestinationProfileIntro || isWeeklyFinanceGuide
        || (isAdvisorGuide && companyAdvisorSelected)
        || (isStatementGuide && financeOverviewSelected)
        || (isQuarterlyGuide && financeReportsSelected)
        || (isAchievementGuide && achievementModalOpen)
        || (isAircraftGuide && aircraftDetailOpen),
      stagePadding: 0,
      stageRadius: 8,
      steps: [{
        element: selector,
        popover: {
          title,
          description,
          showButtons: showNextButton ? ['next', 'close'] : ['close'],
          nextBtnText: '下一步',
          doneBtnText: showNextButton ? '下一步' : '跳过引导',
          onNextClick: showNextButton ? (_element, _step, options) => {
            if (showFleetIntro) setFleetIntroAcknowledged(true);
            if (showFinanceIntro) setFinanceIntroAcknowledged(true);
            if (showDestinationProfileIntro) {
              setDestinationProfileProgress({ routeTargetId, index: destinationProfileIndex + 1 });
            }
            if (showPvgRouteReview) setPvgRouteReviewAcknowledged(true);
            if (isWeeklyFinanceGuide) {
              const nextIndex = weeklyFinanceIndex + 1;
              setWeeklyFinanceProgress({ week: game.week, index: nextIndex });
              if (nextIndex >= 4) {
                const nextPhase = { ...guidePhase, weeklyFinanceComplete: true };
                setGuidePhase(nextPhase);
                writeGuidePhase(game, nextPhase);
              }
            }
            if (isAdvisorGuide && companyAdvisorSelected) {
              const nextPhase = { ...guidePhase, advisorComplete: true };
              setGuidePhase(nextPhase);
              writeGuidePhase(game, nextPhase);
            }
            if (isStatementGuide && financeOverviewSelected) {
              const nextIndex = statementGuideIndex + 1;
              setStatementGuideProgress({ week: game.week, index: nextIndex });
              if (nextIndex >= 3) {
                const nextPhase = { ...guidePhase, statementsComplete: true };
                setGuidePhase(nextPhase);
                writeGuidePhase(game, nextPhase);
              }
            }
            if (isFareGuide && tab === 'routes') {
              const nextPhase = { ...guidePhase, fareGuideComplete: true };
              setGuidePhase(nextPhase);
              writeGuidePhase(game, nextPhase);
            }
            if (isQuarterlyGuide && financeReportsSelected) {
              const nextPhase = { ...guidePhase, quarterlyComplete: true };
              setGuidePhase(nextPhase);
              writeGuidePhase(game, nextPhase);
            }
            if (isAchievementGuide && achievementModalOpen) {
              const nextPhase = { ...guidePhase, achievementsComplete: true };
              setGuidePhase(nextPhase);
              writeGuidePhase(game, nextPhase);
            }
            if (isAircraftGuide && aircraftDetailOpen) {
              const nextIndex = aircraftGuideIndex + 1;
              setAircraftGuideProgress({ week: game.week, index: nextIndex });
              if (nextIndex >= 4) {
                const nextPhase = { ...guidePhase, aircraftGuideComplete: true };
                setGuidePhase(nextPhase);
                writeGuidePhase(game, nextPhase);
              }
            }
            options.driver.destroy();
          } : undefined,
          onPopoverRender: (popover) => {
            popover.closeButton.textContent = '跳过引导';
            popover.closeButton.setAttribute('aria-label', '跳过引导');
            popover.closeButton.tabIndex = -1;
            popover.closeButton.style.width = 'auto';
            popover.closeButton.style.height = 'auto';
            popover.closeButton.style.right = '0';
            popover.closeButton.style.padding = '5px 8px';
            popover.closeButton.style.borderRadius = '8px';
            popover.closeButton.style.color = '#64748b';
            popover.closeButton.style.background = 'transparent';
            popover.closeButton.style.fontSize = '12px';
            popover.closeButton.style.lineHeight = '1.2';
            popover.closeButton.style.whiteSpace = 'nowrap';
            const positionSkipButton = () => {
              const popoverRect = popover.wrapper.getBoundingClientRect();
              const targetRect = document.querySelector('.driver-active-element')?.getBoundingClientRect();
              if (!targetRect) return;
              const isPopoverAboveTarget = popoverRect.bottom <= targetRect.top + 4;
              const isPopoverBelowTarget = popoverRect.top >= targetRect.bottom - 4;
              const placeAbove = isPopoverAboveTarget || (!isPopoverBelowTarget
                && targetRect.top >= window.innerHeight - targetRect.bottom);
              popover.closeButton.style.top = placeAbove ? '-34px' : 'auto';
              popover.closeButton.style.bottom = placeAbove ? 'auto' : '-34px';
            };
            positionSkipButton();
            window.requestAnimationFrame(() => {
              positionSkipButton();
              popover.closeButton.blur();
            });
          },
          onCloseClick: () => {
            setSkipConfirmationOpen(true);
          },
        },
        advanceOnClick,
      }],
    });
    tourRef.current = tour;
    tour.drive();
    return () => {
      tour.destroy();
      if (tourRef.current === tour) tourRef.current = null;
    };
  }, [aircraftGuideProgress, destinationProfileProgress, financeIntroAcknowledged, fleetIntroAcknowledged, game, guidePhase, mobileViewport, pvgRouteReviewAcknowledged, purchaseDialogOpen, routeOriginId, routeTargetId, selectedAirportId, skipped, statementGuideProgress, tab, tourRetry, tutorialEnabled, weeklyFinanceProgress]);

  return {
    onPurchaseDialogChange: setPurchaseDialogOpen,
    tutorialEnabled,
    setTutorialEnabled: (enabled: boolean) => {
      setTutorialEnabledState(enabled);
      setSkipConfirmationOpen(false);
      setSkipped(!enabled);
      if (!enabled) {
        tourRef.current?.destroy();
        tourRef.current = null;
      }
      if (enabled) setTourRetry((value) => value + 1);
    },
    skipConfirmationOpen,
    cancelSkip: () => setSkipConfirmationOpen(false),
    confirmSkip: () => {
      setSkipConfirmationOpen(false);
      setSkipped(true);
      setTutorialEnabledState(false);
      tourRef.current?.destroy();
      tourRef.current = null;
    },
    isGuidedRouteStep: () => {
      if (!game) return false;
      const step = guidedTutorialStep(game);
      return step === 'route-hkg-sha' || step === 'route-hkg-pvg';
    },
    shouldShowCityInfoForAirport: (_iata: string) => false,
    routePlannerReady: true,
  };
}
